import isValid from 'date-fns/isValid';
import max from 'date-fns/max';
import { html, render } from 'lit-html';
import { toLonLat } from 'ol/proj';

import Mediator from 'gisw/Classes/Mediator';
import { getValue, setValue } from 'gisw/Helpers/storage';
import messageObserver from 'gisw/Widgets/MessageManager/MessageObserver';

import { getUpdateInformation } from './utils';
import './bottom-right-menu.less';

class BottomRightMenu {
  domNode = document.createElement('div');

  /** @private */
  isDecimalDegrees = true;

  /**
   * @param {object} config
   * @param {string} config.id
   */
  constructor (config) {
    this.domNode.id = config.id;
    this.domNode.classList.add('LightBasemap');

    /** @private */
    this.mouseCoordinates = this.domNode.appendChild(
      document.createElement('div')
    );
    this.mouseCoordinates.id = 'mouseCoordinates';

    /** @private */
    this.dataUpdateContainer = this.domNode.appendChild(
      document.createElement('div')
    );
      this.dataUpdateContainer.id = 'lastUpdatedContainer';

    /** @private */
    this.OSMCreditContainer = this.domNode.appendChild(
        document.createElement('a'));
    this.OSMCreditContainer.id = 'OSMCreditContainer';

    /** @private */
    this.latlng = this.domNode.appendChild(document.createElement('input'));
    this.latlng.classList.add('js-copytextarea');
    this.latlng.style.display = 'none';
    this.latlng.style.height = '1px';
    this.latlng.style.width = '5px';

    this.showCoordinates = this.showCoordinates.bind(this);

    if (!Mediator.isMobileDevice) {
      Mediator.map.on('pointermove', this.showCoordinates);
    }

      this.initializeCoordinates();
      this.toggleOSMCredit(true);
    getUpdateInformation().then(updates => {
      let updateMessage = '';

      if (updates) {
        const maxDate = max(
          updates
            .filter(entry => entry.updated !== '1/1/0001')
            .map(entry => new Date(entry.updated))
            .filter(entry => isValid(entry))
        );

        if (isValid(maxDate)) {
          updateMessage = 'Site Last Updated: ' + maxDate.toLocaleDateString();
        }
        }

        this.dataUpdateContainer.textContent = updateMessage;
    });
  }

  destroy () {
    Mediator.map.un('pointermove', this.showCoordinates);
    this.domNode.remove();
  }

  /**
   * @param {boolean} isLightBasemap
   */
  toggleIsLightBasemap (isLightBasemap) {
    this.domNode.classList.toggle('LightBasemap', isLightBasemap);
    this.domNode.classList.toggle('DarkBasemap', !isLightBasemap);
    }

    /**
    * @param {boolean} isOSMBasemap
    */
    toggleOSMCredit(isOSMBasemap) {
        if (isOSMBasemap) {
            this.OSMCreditContainer.textContent = '\u00A9 OpenStreetMap';
            this.OSMCreditContainer.href = 'https://www.openstreetmap.org/copyright';
            this.OSMCreditContainer.target = '_blank';
        }
    }

  copyCoordsToClipboard () {
    const copyCommandSupported =
      document.queryCommandSupported && document.queryCommandSupported('copy');

    if (!copyCommandSupported) {
      messageObserver.broadcast({
        title: 'Warning',
        message:
          'Copying latitude/longitude to clipboard ' +
          'is not supported in this browser.'
      });
      return;
    }

    const { longitude, latitude } = Mediator.mapPoint;
    const copyText = latitude + ', ' + longitude;

    this.latlng.value = copyText;
    this.latlng.style.display = '';
    selectText(this.latlng);

    try {
      if (document.execCommand('copy')) {
        messageObserver.broadcast({
          title: 'Information',
          message: copyText + ' is copied to clipboard.'
        });
      }
    } catch (err) {
      Mediator.jstManager.jstError(
        'Widget',
        'BottomRightMenu',
        'CopyLatLngToClipboard',
        err
      );
    }

    this.latlng.style.display = 'none';
  }

  /**
   * @private
   * @param {import('ol/MapBrowserEvent').default} event
   */
  showCoordinates (event) {
    /** @private */
    this.mapPoint = event.coordinate;

    const [long, lat] = toLonLat(event.coordinate);

    Mediator.mapPoint = {
      latitude: lat.toFixed(4),
      longitude: long.toFixed(4)
    };

    if (this.isDecimalDegrees) {
      render(
        html`<p id="mouseCoordinates_latitude">Lat: ${lat.toFixed(6)}</p>
          <p id="mouseCoordinates_longitude">Long: ${long.toFixed(6)}</p>`,
        this.mouseCoordinates
      );
    } else {
      const latDMS = calculateDMS(lat);
      const longDMS = calculateDMS(long);

      render(
        html`<p id="mouseCoordinates_latitude">
            Lat: ${latDMS.degrees}&deg; ${latDMS.minutes}'
            ${latDMS.seconds.toFixed(4)}"
          </p>
          <p id="mouseCoordinates_longitude">
            Long: ${longDMS.degrees}&deg; ${longDMS.minutes}'
            ${longDMS.seconds.toFixed(4)}"
          </p>`,
        this.mouseCoordinates
      );
    }
  }

  /**
   * @private
   * Switches between degrees, minutes, seconds and decimal degrees
   */
  toggleLatLongStyle () {
    if (this.isDecimalDegrees) {
      this.showDegreesMinutesSeconds();
    } else {
      this.showDecimalDegrees();
    }

    this.showCoordinates({ coordinate: this.mapPoint });
  }

  /**
   * @private
   */
  showDegreesMinutesSeconds () {
    this.domNode.title = 'Click to toggle Decimal Degrees';
    this.isDecimalDegrees = false;
    setValue('showDegreesMinutesSeconds', true);
  }

  /**
   * @private
   */
  showDecimalDegrees () {
    this.domNode.title = 'Click to toggle Degrees, Minutes, and Seconds';
    this.isDecimalDegrees = true;
    setValue('showDegreesMinutesSeconds', false);
  }

  /**
   * @private
   */
  initializeCoordinates () {
    this.domNode.addEventListener(
      'click',
      () => {
        this.toggleLatLongStyle();
      },
      { passive: true }
    );

    if (getValue('showDegreesMinutesSeconds')) {
      this.showDegreesMinutesSeconds();
    } else {
      this.showDecimalDegrees();
    }
  }
}

export default BottomRightMenu;

/**
 * @param {HTMLInputElement} element
 */
function selectText (element) {
  if (/INPUT|TEXTAREA/i.test(element.tagName)) {
    element.focus();

    if (element.setSelectionRange) {
      element.setSelectionRange(0, element.value.length);
    } else {
      element.select();
    }

    return;
  }

  const rangeObj = document.createRange();
  rangeObj.selectNodeContents(element);

  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(rangeObj);
}

/**
 * @param {number} decimalCoord
 */
function calculateDMS (decimalCoord) {
  const degrees =
    decimalCoord < 0 ? Math.ceil(decimalCoord) : Math.floor(decimalCoord);

  let remainder = (Math.abs(decimalCoord) - Math.abs(degrees)) * 60;
  const minutes = Math.floor(remainder);
  remainder -= minutes;

  const seconds = remainder * 60;
  return { degrees, minutes, seconds };
}
